// markup/model.mjs — DropPin plan-markup DATA MODEL (pure, headless).
// ---------------------------------------------------------------------------
// The serialisable annotation object model for DropPin's plan markup + measure
// toolset. NO React / DOM here — every export is pure and node-testable. All
// geometry is delegated to the shared engine (@insitebg/plan-markup-core), never
// re-implemented, so DropPin measures identically to InSpecPDF.
//
// COORDINATE MODEL — every markup point is stored NORMALISED (0..1) in the
// drawing's natural-image space, exactly like DropPin defect pins (markX/markY).
// This is resolution-independent and portable: it survives the plan being
// re-rastered at a different DPI and serialises to plain JSON for later
// persist/sync (the "annotation data service, data-only, render on-device"
// convergence design). Measurement converts normalised -> natural PIXEL space
// (x*W, y*H) because pixel space is ISOTROPIC (the viewer scales x and y by one
// factor); normalised space is not, so lengths/areas must never be taken in it.
// ---------------------------------------------------------------------------

import {
  distance,
  pointInPolygon,
  distanceToSegment,
  polygonCentroid,
  bbox,
} from '@insitebg/plan-markup-core';
import { cleanTypeKey, cleanColor, sanitizeLegendRows, legendBoxNorm, clampLegendFont } from './legend.mjs';

export const MARKUP_SCHEMA_VERSION = 1;

// Tool identifiers (also the shape `type` for the drawing tools).
export const TOOL = {
  SELECT: 'select',
  PAN: 'pan',
  LINE: 'line',
  ARROW: 'arrow',
  RECT: 'rect',
  POLYGON: 'polygon',
  POLYLINE: 'polyline',
  FREEHAND: 'freehand',
  TEXT: 'text',
  MEASURE: 'measure',
  CALIBRATE: 'calibrate',
  LEGEND: 'legend',
};

// Persisted object `type` values.
export const OBJECT_TYPES = [
  'line', 'arrow', 'rect', 'polygon', 'polyline', 'freehand', 'text', 'measure', 'legend',
];

// Default palette — brand orange for markup, blue for measure (reads as a
// distinct "dimension" layer). Colours are DATA (portable JSON), not UI CSS,
// so literal values belong here; the React chrome uses theme tokens.
export const DEFAULT_STROKE = '#e8650a';
export const MEASURE_STROKE = '#2563eb';

export function defaultStyle(type) {
  const base = { stroke: DEFAULT_STROKE, strokeWidth: 2, opacity: 1 };
  if (type === 'rect' || type === 'polygon') {
    return { ...base, fill: DEFAULT_STROKE, fillOpacity: 0.12 };
  }
  if (type === 'text') {
    return { stroke: DEFAULT_STROKE, fill: '#0f172a', fontSize: 15, strokeWidth: 0 };
  }
  if (type === 'measure') {
    return { stroke: MEASURE_STROKE, strokeWidth: 2, fill: MEASURE_STROKE, fillOpacity: 0.10, opacity: 1 };
  }
  // A legend's fontSize is in the drawing's natural pixels (a stamp that zooms
  // with the plan), unlike a text note's, which is screen pixels.
  if (type === 'legend') {
    return { stroke: '#0f172a', fill: '#ffffff', fontSize: 14, strokeWidth: 1, opacity: 1 };
  }
  return base;
}

// Unique-enough id for a markup object. Injectable in tests via createObject.
let __seq = 0;
export function genId() {
  __seq = (__seq + 1) % 1e6;
  return 'mk_' + Date.now().toString(36) + '_' + __seq.toString(36) + Math.random().toString(36).slice(2, 6);
}

function clonePoints(points) {
  return (points || []).map((p) => ({ x: p.x, y: p.y }));
}

// Factory. `type` is one of OBJECT_TYPES.
// opts: { id, points, text, style, measureKind, label, typeKey, rows, totals }.
export function createObject(type, opts = {}) {
  const o = {
    id: opts.id || genId(),
    type,
    points: clonePoints(opts.points),
    style: { ...defaultStyle(type), ...(opts.style || {}) },
  };
  if (type === 'text') o.text = opts.text || '';
  if (type === 'measure') o.measureKind = opts.measureKind === 'area' ? 'area' : 'length';
  if (opts.label != null && String(opts.label).trim()) o.label = String(opts.label).slice(0, 120);
  // 2026-09-15 — the catalogue type was handed in here and never set, so a mark
  // drawn with "New tiles" armed was saved as a plain area with nothing to total
  // it by. sanitizeInObject carried the key; this factory dropped it first.
  const typeKey = cleanTypeKey(opts.typeKey);
  if (typeKey) o.typeKey = typeKey;
  if (type === 'legend') {
    o.rows = sanitizeLegendRows(opts.rows);
    if (opts.totals === false) o.totals = false;
    o.style.fontSize = clampLegendFont(o.style.fontSize) || 14;
  }
  return o;
}

// Deep-ish clone (points + style copied so mutation never leaks). Optionally
// re-id (used by copy/paste to make an independent object).
export function cloneObject(o, newId) {
  const c = {
    ...o,
    id: newId || o.id,
    points: clonePoints(o.points),
    style: { ...o.style },
  };
  if (Array.isArray(o.rows)) c.rows = o.rows.map((r) => ({ ...r }));
  return c;
}

// 2026-09-15 — how a closed mark wears its catalogue type's colour, whether it
// was drawn with the type armed or given the type afterwards.
export const TYPED_FILL_OPACITY = 0.2;

// Which family of catalogue types a mark takes: 'area' for an area measure,
// 'length' for a length measure, 'other' (the kinds drawn as polygons) for a
// polygon, null for anything else — it has no type to change.
export function markTypeKind(o) {
  if (!o) return null;
  if (o.type === 'measure') return o.measureKind === 'area' ? 'area' : 'length';
  return o.type === 'polygon' ? 'other' : null;
}

// The tenant's types that fit this mark, active ones only.
export function markTypesFor(o, markTypes) {
  const want = markTypeKind(o);
  if (!want) return [];
  return (markTypes || []).filter((t) => t && t.active !== false && cleanTypeKey(t.key) && cleanColor(t.color)
    && (want === 'other' ? (t.kind !== 'area' && t.kind !== 'length') : t.kind === want));
}

// 2026-10-21 — Brett: "we should be able to select multiple areas and change its
// type at once." What one pick changes when several marks are selected: every
// selected mark of the same family as the one pressed (`anchor`), or, from the
// toolbar with nothing pressed, of the family most of the selection is (areas
// first on a tie). A text or a line in the selection is left as it is — an area
// type means nothing on it. `currentKey` is ticked only when every target has
// that one type; `anyTyped` offers "No type" when at least one has a type.
const KIND_ORDER = ['area', 'length', 'other'];
export function retypePlan(selected, anchor, markTypes) {
  const list = (selected || []).filter(Boolean);
  let kind = anchor ? markTypeKind(anchor) : null;
  if (!anchor) {
    let best = 0;
    for (const k of KIND_ORDER) {
      const n = list.filter((o) => markTypeKind(o) === k).length;
      if (n > best) { best = n; kind = k; }
    }
  }
  const targets = kind ? list.filter((o) => markTypeKind(o) === kind) : [];
  const types = targets.length ? markTypesFor(targets[0], markTypes) : [];
  const keys = new Set(targets.map((o) => o.typeKey || null));
  return {
    kind,
    targets,
    types,
    currentKey: keys.size === 1 ? [...keys][0] : null,
    anyTyped: targets.some((o) => !!o.typeKey),
    selected: list.length,
  };
}

// Each patch merged onto the mark with its id; marks with no patch, and patches
// with no mark, change nothing.
export function mergePatches(objects, patches) {
  const byId = new Map((patches || []).filter((p) => p && p.id != null).map((p) => [String(p.id), p]));
  return (objects || []).map((o) => (byId.has(String(o.id)) ? { ...o, ...byId.get(String(o.id)) } : o));
}

// How many marks a pick changes, in words: '' for the one mark pressed alone;
// "3 areas" for several; "3 areas of 5 selected" when some are left out.
const KIND_WORD = { area: 'area', length: 'length', other: 'outline' };
export function retypeCount(plan) {
  const n = plan && plan.targets ? plan.targets.length : 0;
  const selected = (plan && plan.selected) || 0;
  if (!n || (n === 1 && selected <= 1)) return '';
  const word = `${n} ${KIND_WORD[plan.kind] || 'mark'}${n === 1 ? '' : 's'}`;
  return selected > n ? `${word} of ${selected} selected` : word;
}

// The menu's heading.
export function retypeTitle(plan) {
  const count = retypeCount(plan);
  return count ? `Mark type · ${count}` : 'Mark type';
}

// 2026-10-21 — Brett, the corridor area selected and the yellow Full regrout
// button pressed: "what do i push to change the tag to full regrout?", then
// "yes make it work that way." A type button on the toolbar works like a
// word processor's: with marks selected that the type fits, a press changes
// them (one undo step) and the button lights when every one of them has it;
// with none of them selected, it arms the type for the next shape drawn, as
// before. `changes` are the fits that do not have it yet. A press when every
// fit already has it changes nothing: taking a type off is the menu's "No type".
export function typeChipPlan(selected, t) {
  const key = t ? cleanTypeKey(t.key) : null;
  const fits = key ? (selected || []).filter((o) => o && markTypesFor(o, [t]).length > 0) : [];
  const changes = fits.filter((o) => (o.typeKey || null) !== key);
  return { fits, changes, on: fits.length > 0 && changes.length === 0 };
}

// The button's hover words while it is acting on the selection.
export function typeChipTitle(chip, t) {
  const n = chip && chip.fits ? chip.fits.length : 0;
  if (!n || !t) return '';
  const word = KIND_WORD[markTypeKind(chip.fits[0])] || 'mark';
  const what = n === 1 ? `the selected ${word}` : `the ${n} selected ${word}s`;
  return chip.on
    ? `${what.charAt(0).toUpperCase()}${what.slice(1)} ${n === 1 ? 'is' : 'are'} ${t.label}`
    : `Change ${what} to ${t.label}`;
}

// Brett: "double click an area and assign it to one of the shortcut measures.
// and change it from one to another". The type and its colour go together, so
// changing one changes the other; `null` takes the type off and gives the mark
// back its default colour. A type that is not a valid catalogue entry changes
// nothing. Returns the whole object, with typeKey written as undefined when
// cleared, so merging it onto the old mark removes the key as well.
export function withMarkType(o, t) {
  if (!o) return o;
  const key = t ? cleanTypeKey(t.key) : null;
  const color = t ? cleanColor(t.color) : null;
  if (t && !(key && color)) return o;
  const next = { ...o, style: { ...(o.style || {}) } };
  if (t) {
    next.typeKey = key;
    next.style.stroke = color;
    if (isClosed(o)) { next.style.fill = color; next.style.fillOpacity = TYPED_FILL_OPACITY; }
  } else {
    const base = defaultStyle(o.type);
    next.typeKey = undefined;
    next.style.stroke = base.stroke;
    if (isClosed(o) && base.fill) { next.style.fill = base.fill; next.style.fillOpacity = base.fillOpacity; }
  }
  return next;
}

// Immutable translate by (dx,dy) in NORMALISED units.
export function translateObject(o, dx, dy) {
  return {
    ...o,
    points: (o.points || []).map((p) => ({ x: p.x + dx, y: p.y + dy })),
    style: { ...o.style },
  };
}

// True for filled / closed shapes (area-like); false for open strokes / text.
export function isClosed(o) {
  return (
    o.type === 'rect' ||
    o.type === 'polygon' ||
    (o.type === 'measure' && o.measureKind === 'area')
  );
}

// Normalised -> pixel point list, given the drawing's natural size.
export function toPixels(points, W, H) {
  return (points || []).map((p) => ({ x: p.x * W, y: p.y * H }));
}

// Pixel rect {x,y,w,h} from a 2-corner shape's pixel points.
function pixRect(pts) {
  const x = Math.min(pts[0].x, pts[1].x);
  const y = Math.min(pts[0].y, pts[1].y);
  return { x, y, w: Math.abs(pts[1].x - pts[0].x), h: Math.abs(pts[1].y - pts[0].y) };
}

// Rect as normalised {x,y,w,h} (NW corner + size) for rendering.
export function rectXYWH(o) {
  const a = o.points[0], b = o.points[1] || o.points[0];
  return { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(b.x - a.x), h: Math.abs(b.y - a.y) };
}

// Normalised bounding box {minX,minY,maxX,maxY}. Uses core bbox (which takes
// [x,y] PAIRS), padded by `padNorm`.
export function boundsOf(o, padNorm = 0) {
  if (!o.points || !o.points.length) return null;
  const pairs = o.points.map((p) => [p.x, p.y]);
  const [minX, minY, maxX, maxY] = bbox(pairs, padNorm);
  return { minX, minY, maxX, maxY };
}

// Pixel centroid (label anchor) via the core's vertex-mean centroid.
export function centroidPx(o, W, H) {
  return polygonCentroid(toPixels(o.points, W, H));
}

// Hit-test an object at pixel point `pPx` with pixel tolerance `tolPx`.
// All distance / containment math is the shared core's — never re-derived.
export function hitTest(o, pPx, tolPx, W, H) {
  const pts = toPixels(o.points, W, H);
  if (!pts.length) return false;

  if (o.type === 'legend') {
    const b = legendBoxNorm(o, W, H);
    return (
      pPx.x >= b.minX * W - tolPx && pPx.x <= b.maxX * W + tolPx &&
      pPx.y >= b.minY * H - tolPx && pPx.y <= b.maxY * H + tolPx
    );
  }

  if (o.type === 'text') {
    const a = pts[0];
    const fs = o.style && o.style.fontSize ? o.style.fontSize : 15;
    const w = Math.max(fs, (o.text ? o.text.length : 1) * fs * 0.6);
    return (
      pPx.x >= a.x - tolPx && pPx.x <= a.x + w + tolPx &&
      pPx.y >= a.y - fs - tolPx && pPx.y <= a.y + tolPx
    );
  }

  if (o.type === 'rect') {
    const r = pixRect(pts);
    return (
      pPx.x >= r.x - tolPx && pPx.x <= r.x + r.w + tolPx &&
      pPx.y >= r.y - tolPx && pPx.y <= r.y + r.h + tolPx
    );
  }

  if (isClosed(o)) {
    if (pts.length >= 3 && pointInPolygon(pPx, pts)) return true;
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i], b = pts[(i + 1) % pts.length];
      if (distanceToSegment(pPx, a, b) <= tolPx) return true;
    }
    return false;
  }

  // Open stroke (line / arrow / polyline / freehand / measure-length).
  if (pts.length === 1) return distance(pPx, pts[0]) <= tolPx;
  for (let i = 1; i < pts.length; i++) {
    if (distanceToSegment(pPx, pts[i - 1], pts[i]) <= tolPx) return true;
  }
  return false;
}

// Topmost (last-drawn) object hit at pPx, or null.
export function hitTestTopmost(objects, pPx, tolPx, W, H) {
  for (let i = objects.length - 1; i >= 0; i--) {
    if (hitTest(objects[i], pPx, tolPx, W, H)) return objects[i].id;
  }
  return null;
}

// ---- Serialisation --------------------------------------------------------

function finite(n) { return typeof n === 'number' && isFinite(n); }

function sanitizeInObject(o) {
  if (!o || typeof o !== 'object') return null;
  if (!OBJECT_TYPES.includes(o.type)) return null;
  const points = Array.isArray(o.points)
    ? o.points.filter((p) => p && finite(p.x) && finite(p.y)).map((p) => ({ x: p.x, y: p.y }))
    : [];
  if (!points.length) return null;
  const out = {
    id: o.id || genId(),
    type: o.type,
    points,
    style: (o.style && typeof o.style === 'object') ? { ...defaultStyle(o.type), ...o.style } : defaultStyle(o.type),
  };
  if (o.type === 'text') out.text = typeof o.text === 'string' ? o.text : '';
  if (o.type === 'measure') out.measureKind = o.measureKind === 'area' ? 'area' : 'length';
  if (o.label != null && String(o.label).trim()) out.label = String(o.label).slice(0, 120);
  // 2026-08-14 — WHAT KIND OF THING THIS MARK IS, from the tenant's mark
  // catalogue ("new-tiles", "full-regrout"). This is what lets measurements
  // total BY TYPE — "new tiles 42 m², full regrout 18 m" — rather than being one
  // undifferentiated pile of areas.
  //
  // It MUST be carried here or the feature dies silently: this function is an
  // allow-list, so anything it does not name is dropped on save. The toolbar
  // would work perfectly all session and lose every type on reload.
  //
  // Validated to the catalogue's key shape rather than trusted, because a mark
  // can arrive from an imported document as easily as from our own toolbar.
  const typeKey = cleanTypeKey(o.typeKey);
  if (typeKey) out.typeKey = typeKey;
  // 2026-09-15 — a legend lists what it was stamped with. Rows are validated one
  // by one (key shape, a real colour, a name); a legend left with none has
  // nothing to say and is dropped rather than drawn as an empty box.
  if (o.type === 'legend') {
    out.rows = sanitizeLegendRows(o.rows);
    if (!out.rows.length) return null;
    if (o.totals === false) out.totals = false;
    out.style.fontSize = clampLegendFont(out.style.fontSize) || 14;
  }
  return out;
}

export function validCalibration(c) {
  if (!c || typeof c !== 'object') return null;
  if (!c.refA || !c.refB) return null;
  if (!finite(c.refA.x) || !finite(c.refA.y) || !finite(c.refB.x) || !finite(c.refB.y)) return null;
  if (!finite(c.knownMm) || c.knownMm <= 0) return null;
  return { refA: { x: c.refA.x, y: c.refA.y }, refB: { x: c.refB.x, y: c.refB.y }, knownMm: c.knownMm };
}

// Doc -> plain JSON (safe to POST / localStorage / sync later).
export function serialize(doc) {
  return {
    version: MARKUP_SCHEMA_VERSION,
    objects: (doc && Array.isArray(doc.objects) ? doc.objects : []).map((o) => sanitizeInObject(o)).filter(Boolean),
    calibration: validCalibration(doc && doc.calibration),
    unit: (doc && doc.unit) || null,
  };
}

// JSON -> doc. Drops malformed objects rather than throwing.
export function deserialize(json) {
  return {
    version: (json && json.version) || MARKUP_SCHEMA_VERSION,
    objects: (json && Array.isArray(json.objects) ? json.objects : []).map((o) => sanitizeInObject(o)).filter(Boolean),
    calibration: validCalibration(json && json.calibration),
    unit: (json && json.unit) || null,
  };
}
